import { Component, OnInit, ChangeDetectionStrategy, SimpleChanges, Input, Output, EventEmitter, ContentChildren, QueryList, TemplateRef } from '@angular/core';
import { IDataGridDefinition, IDataGridSource, IDataGridEvent, IDataGridButtonEvent } from '../../interfaces/grid';
import { TemplateDirective } from '../../directives/template.directive';
import { GridType } from '../../enums';
import { animate, state, style, transition, trigger } from '@angular/animations';


@Component({
  selector: 'lib-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
    trigger('enterAnimation', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('500ms', style({ transform: 'translateX(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ transform: 'translateX(0)', opacity: 1 }),
        transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      ])
    ])
  ]
})
export class GridComponent implements OnInit {

  @Input() emptyRowsMsg: string = 'No se ha encontrado ningún resultado. Intenta con otra búsqueda';
  @Input() loading: boolean;
  @Input() definition: IDataGridDefinition;
  @Input() source: IDataGridSource<any>;
  @Input() pageSizeOptions = [10, 25, 50, 100];
  @Output() loadData: EventEmitter<IDataGridEvent> = new EventEmitter();
  @Input() noElevation: boolean = false;
  @ContentChildren(TemplateDirective) templates: QueryList<TemplateDirective>;
  @Input() serverError?: any = null;
  @Input() tableWidth: number = null;
  @Input() variant: GridType = GridType.SERVER;
  @Input() matElevation: number = 8;
  @Input() expandableRows: boolean = false;
  @Input() showRowIndex: boolean = true;
  @Input() fixedLayout: boolean = true;
  @Input() hideNumberButtons: boolean = false;
  // @Input() rowSpanRowIndex: number = 1;


  gridType = GridType;
  displayedColumns: string[];
  customTemplates: any = {};
  anyEditable: boolean = false;

  /**
   * objeto que alamacena los indices de las filas y su estado true: abierto, false: cerrado
   */
  expandableState: any = {};

  /**
   * objeto que alamacena los datos para grilla editable por file
   * ejem: editWorkData[0]: alamacena los datos de edición para la fila 0
   */
  editWorkData: any = {};

  expandableTemplate: TemplateRef<any>;

  constructor() { }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.definition && changes.definition.currentValue) {
      this.displayedColumns = [
        ...(this.expandableRows ? ['expandable-button-column'] : []),
        ...(this.showRowIndex ? ['row-index-column'] : []),
        ...this.definition.columns.filter(
          x => x.hidden !== true
        ).map(
          x => x.field
        )
      ];
    }
  }

  ngAfterContentInit(): void {
    const customColumns = this.definition.columns.filter(x => x.template);

    if (customColumns.length > 0) {
      customColumns.forEach(x => {
        const matches = this.templates.filter(temp => temp.getType() == x.template);
        if (matches.length > 0) {
          this.customTemplates[x.template] = matches[0].template;
        }
      });
    }

    this.setExpandableTemplate();
  }

  handleChangePage = (page) => {
    this.resetExpandableState();
    this.loadData.emit({
      page,
      pageSize: this.source.pageSize,
      orderBy: this.source.orderBy,
      orderDir: this.source.orderDir,
      skip: this.calcSkipByPage(page, this.source.pageSize)
    });
  }

  handleSort = (event) => {
    const fieldActive = this.definition.columns.find(x => x.field == event.active);
    this.resetExpandableState();
    this.loadData.emit({
      page: this.source.page,
      pageSize: this.source.pageSize,
      orderBy: fieldActive.sortableField || event.active,
      orderDir: event.direction,
      skip: this.calcSkipByPage(this.source.page, this.source.pageSize)
    });
  }

  handleChangePageSize = (pageSize) => {
    const page = 1;
    this.resetExpandableState();
    this.loadData.emit({
      page: page,
      pageSize: pageSize,
      orderBy: this.source.orderBy,
      orderDir: this.source.orderDir,
      skip: this.calcSkipByPage(page, pageSize)
    });
  }

  private calcSkipByPage = (page: number, pageSize: number): number => {
    return (page - 1) * pageSize;
  }

  handleClickSave = (rowIndex: number) => {

  }

  get gridContainerClass() {
    return { ['mat-elevation-z' + this.matElevation]: !this.noElevation, 'overflow-x': this.tableWidth != null };
  }

  toogleExpandableRow = (rowIndex: number) => {
    if (this.isRowExpanded(rowIndex)) {
      this.handleCloseExpandable(rowIndex);
    } else {
      this.handleOpenExpandable(rowIndex);
    }
  }

  handleOpenExpandable = index => {
    this.expandableState[index] = true;
  };

  handleCloseExpandable = index => {
    this.expandableState[index] = false;
  };

  resetExpandableState = () => {
    this.expandableState = {};
  };

  isRowExpanded = rowIndex => {
    return (
      this.expandableState[rowIndex] && this.expandableState[rowIndex] === true
    );
  };

  calcNroRow = (indexRow: number): number => {
    const { page, pageSize } = this.source;
    return indexRow + 1 + (page - 1) * pageSize;
  };

  private setExpandableTemplate = () => {
    const match = this.templates.find(
      temp => temp.getType() === 'expandable-row'
    );
    if (match) {
      this.expandableTemplate = match.template;
    }
  };

  getOrderByField = () => {
    if (!this.source.orderBy) {
      return null;
    }
    const fieldActive = this.definition.columns.find(x => x.field == this.source.orderBy || x.sortableField == this.source.orderBy);
    return fieldActive.field;
  }

}
